export class Vector2d {
  constructor(
    readonly x: number,
    readonly y: number,
  ) { }

  public static add(a: Vector2d, b: Vector2d) {
    return new Vector2d(a.x + b.x, a.y + b.y);
  }

  public static multiply(vector: Vector2d, scalar: number) {
    return new Vector2d(vector.x * scalar, vector.y * scalar);
  }

  public static divide(vector: Vector2d, scalar: number) {
    return Vector2d.multiply(vector, 1 / scalar);
  }

  public equals(other: Vector2d) {
    return (this.x === other.x && this.y === other.y);
  }

  /**
   * Returns the magnitude of this vector squared.
   * This is a faster operation than calculating the ordinary magnitude,
   * as there is no square root operation involved.
   */
  public magnitudeSq(): number {
    return Math.pow(this.x, 2) + Math.pow(this.y, 2);
  }

  /**
   * Returns the magnitude of this vector.
   * This is a slow operation due to involving a square root.
   * Prefer comparing `this.magnitudeSq()` if possible.
   */
  public magnitude(): number {
    return Math.sqrt(this.magnitudeSq());
  }

  public toString(): string {
    return `Vector2d [${this.x},${this.y}]`;
  }
}

// An AABB is an axis-aligned bounding box.
export class AABB {

  public get right(): number {
    return this.x + this.width;
  }

  public get bottom(): number {
    return this.y + this.height;
  }

  public get centerX(): number {
    return this.x + (this.width / 2);
  }

  public get centerY(): number {
    return this.y + (this.height / 2);
  }

  constructor(
    public x: number,
    public y: number,
    public width: number,
    public height: number,
  ) { }

  // Create a new AABB based on TRBL coordinates. (Top, Right, Bottom, Left.)
  public static fromTRBL(top: number, right: number, bottom: number, left: number): AABB {
    const w = right - left;
    const h = bottom - top;

    return new AABB(left, top, w, h);
  }

  public static fromCenter(centerX: number, centerY: number, width: number, height: number): AABB {
    const x = centerX - (width / 2);
    const y = centerY - (height / 2);

    return new AABB(x, y, width, height);
  }

  public equals(other: AABB): boolean {
    return (
      this.x === other.x
      && this.y === other.y
      && this.width === other.width
      && this.height === other.height
    );
  }

  // Returns whether this AABB wholly contains another AABB.
  // This is defined as no point of the other AABB being outside this AABB.
  // (This means an AABB would be considered to contain itself.)
  public containsAABB(other: AABB): boolean {
    if (other.x < this.x) { return false; }
    if (other.y < this.y) { return false; }
    if (other.right > this.right) { return false; }
    if (other.bottom > this.bottom) { return false; }

    return true;
  }

  public containsVector(vec: Vector2d): boolean {
    if (vec.x > this.right) { return false; }
    if (vec.x < this.x) { return false; }
    if (vec.y > this.bottom) { return false; }
    if (vec.y < this.y) { return false; }

    return true;
  }

  public toString(): string {
    const min = `${this.x.toFixed(2)}, ${this.y.toFixed(2)}`;
    const max = `${this.right.toFixed(2)}, ${this.bottom.toFixed(2)}`;
    const wh = `${this.width.toFixed(2)} x ${this.height.toFixed(2)}`;
    return `AABB [min ${min}; max ${max}; w/h ${wh}]`;
  }
}
